import { useState } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Nfc,
  CheckCircle,
  AlertCircle,
  Loader2,
  Info,
  Copy,
  Check,
  Smartphone
} from 'lucide-react';
import { toast } from 'sonner';
import { useNFC } from '@/hooks/useNFC';

/**
 * Component for writing check-in URLs to NFC tags
 * Allows admins to program NFC tags for quick tap-to-check-in
 * Shows URL details for use with third-party NFC writing apps
 */
export function NFCTagWriter({ checkInUrl, siteName }) {
  const { supported, writing, error, success, writeTag, reset } = useNFC();
  const [showInstructions, setShowInstructions] = useState(false);
  const [copied, setCopied] = useState(false);

  // Add source=nfc parameter to the URL
  const nfcUrl = checkInUrl
    ? `${checkInUrl}${checkInUrl.includes('?') ? '&' : '?'}source=nfc`
    : null;

  const handleWriteTag = async () => {
    if (!nfcUrl) {
      toast.error('No check-in URL available');
      return;
    }

    reset();
    setShowInstructions(true);

    const result = await writeTag(nfcUrl);

    if (result) {
      toast.success('NFC tag written successfully!');
      setShowInstructions(false);
    }
  };

  const handleCopyUrl = async () => {
    if (!nfcUrl) return;

    try {
      await navigator.clipboard.writeText(nfcUrl);
      setCopied(true);
      toast.success('URL copied to clipboard');
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      toast.error('Failed to copy URL');
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-2">
          <Nfc className="h-5 w-5 text-blue-600" />
          <CardTitle className="text-base">NFC Tag</CardTitle>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Description */}
        <p className="text-sm text-gray-600">
          Program an NFC tag with the check-in URL. Users can tap their phone on the tag
          to quickly check in to <span className="font-medium">{siteName || 'this site'}</span>.
        </p>

        {/* URL to Write Section - Always visible */}
        <div className="space-y-2">
          <Label className="text-sm font-medium">URL to Write to NFC Tag</Label>
          <div className="flex gap-2">
            <Input
              value={nfcUrl || ''}
              readOnly
              className="font-mono text-xs bg-gray-50"
            />
            <Button
              variant="outline"
              size="icon"
              onClick={handleCopyUrl}
              disabled={!nfcUrl}
              className="flex-shrink-0"
            >
              {copied ? (
                <Check className="h-4 w-4 text-green-600" />
              ) : (
                <Copy className="h-4 w-4" />
              )}
            </Button>
          </div>
          <p className="text-xs text-gray-500">
            Copy this URL and use it with any NFC writing app
          </p>
        </div>

        {/* Native Write Button - Only show if supported */}
        {supported && (
          <div className="pt-2 border-t">
            <p className="text-xs text-gray-500 mb-3">
              Or write directly from this browser (Chrome on Android):
            </p>

            {/* Success State */}
            {success && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-3">
                <div className="flex items-start gap-2">
                  <CheckCircle className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" />
                  <div className="text-sm">
                    <p className="font-medium text-green-800">Tag Written Successfully!</p>
                    <p className="text-green-700 text-xs mt-0.5">
                      Users can now tap their phone on the tag to check in.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Error State */}
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-3">
                <div className="flex items-start gap-2">
                  <AlertCircle className="h-4 w-4 text-red-600 flex-shrink-0 mt-0.5" />
                  <div className="text-sm">
                    <p className="font-medium text-red-800">Write Failed</p>
                    <p className="text-red-700 text-xs mt-0.5">{error}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Writing State */}
            {showInstructions && writing && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-3">
                <div className="flex items-start gap-2">
                  <Loader2 className="h-4 w-4 text-blue-600 animate-spin flex-shrink-0 mt-0.5" />
                  <div className="text-sm">
                    <p className="font-medium text-blue-800">Ready to Write</p>
                    <p className="text-blue-700 text-xs mt-0.5">
                      Hold your phone near the NFC tag now...
                    </p>
                  </div>
                </div>
              </div>
            )}

            <Button
              onClick={handleWriteTag}
              disabled={writing || !nfcUrl}
              className="w-full"
              variant={success ? 'outline' : 'default'}
            >
              {writing ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Waiting for NFC tag...
                </>
              ) : success ? (
                <>
                  <Nfc className="h-4 w-4 mr-2" />
                  Write Another Tag
                </>
              ) : (
                <>
                  <Nfc className="h-4 w-4 mr-2" />
                  Write to NFC Tag
                </>
              )}
            </Button>
          </div>
        )}

        {/* Instructions for Third-Party Apps */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
          <div className="flex items-start gap-2">
            <Smartphone className="h-4 w-4 text-blue-600 flex-shrink-0 mt-0.5" />
            <div className="text-xs text-blue-800">
              <p className="font-medium mb-1">Using iPhone or other devices?</p>
              <ol className="list-decimal list-inside space-y-0.5 text-blue-700">
                <li>Download "NFC Tools" app (free on App Store/Play Store)</li>
                <li>Open the app and tap "Write"</li>
                <li>Select "Add a record" → "URL/URI"</li>
                <li>Paste the URL above</li>
                <li>Tap "Write" and hold phone to NFC tag</li>
              </ol>
            </div>
          </div>
        </div>

        {/* Tips */}
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
          <div className="flex items-start gap-2">
            <Info className="h-4 w-4 text-gray-500 flex-shrink-0 mt-0.5" />
            <div className="text-xs text-gray-600">
              <p className="font-medium text-gray-700 mb-1">Tips for NFC tags:</p>
              <ul className="list-disc list-inside space-y-0.5">
                <li>Use NTAG213 or NTAG215 tags (most common, ~$0.50 each)</li>
                <li>Place tags at eye level near the entrance</li>
                <li>Tags can be rewritten if you need to update the URL</li>
                <li>Test the tag with your phone before mounting</li>
              </ul>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export default NFCTagWriter;
